// src/pages/MPDashboard/index.jsx
// Rota dedicada: /mp/dashboard/:campaignId
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../lib/apiClient';
import AppLayout from '../../layouts/AppLayout';
import { DSButton, DSBadge, DSPageContainer, IceCard } from '../../design-system/components';
import './MPDashboard.css';

function BarChart({ dates, impressions, clicks }) {
  const W = 640, H = 180, PL = 40, PR = 12, PT = 12, PB = 28;
  const cW = W - PL - PR;
  const cH = H - PT - PB;
  const n  = dates.length;
  if (n === 0) return (
    <p className="mpd-chart__empty">Sem dados disponíveis</p>
  );

  const maxV = Math.max(...impressions, 1);
  const bw   = Math.max(5, (cW / n) * 0.5);
  const gap  = cW / n;
  const bx   = (i) => PL + i * gap + gap / 2 - bw / 2;
  const by   = (v) => PT + cH - (v / maxV) * cH;
  const bh   = (v) => Math.max(1, (v / maxV) * cH);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto" className="mpd-chart__svg">
      {impressions.map((v, i) => (
        <rect key={`i${i}`} x={bx(i)} y={by(v).toFixed(1)}
          width={bw} height={bh(v).toFixed(1)} rx="3" className="mpd-chart__bar--imp" />
      ))}
      {clicks.map((v, i) => (
        <rect key={`c${i}`} x={bx(i) + bw * 0.6} y={by(v).toFixed(1)}
          width={bw * 0.6} height={bh(v).toFixed(1)} rx="2" className="mpd-chart__bar--clk" />
      ))}
      {dates.map((d, i) => (
        <text key={i} x={bx(i) + bw / 2} y={H - 6}
          textAnchor="middle" className="mpd-chart__label">
          {d.slice(5)}
        </text>
      ))}
    </svg>
  );
}

function StatCard({ label, value, color }) {
  return (
    <IceCard compact noHover className="mpd-stat">
      <div className="mpd-stat__value" style={{ color }}>{value}</div>
      <div className="mpd-stat__label">{label}</div>
    </IceCard>
  );
}

function MPDashboardContent() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [camp, setCamp]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    Promise.all([
      apiClient.request(`/mp/dashboard/${campaignId}`, { method: 'GET' }),
      apiClient.request(`/mp/campaign/${campaignId}`, { method: 'GET' }),
    ]).then(([dashRes, campRes]) => {
      setData(dashRes);
      setCamp(campRes?.campaign);
    }).catch(() => {
      setError('Erro ao carregar dados da campanha.');
    }).finally(() => setLoading(false));
  }, [campaignId]);

  if (loading) return <IceCard className="mpd-loading">Carregando...</IceCard>;
  if (error)   return <IceCard className="mpd-error">{error}</IceCard>;

  const perf        = data?.performance || {};
  const dates       = perf.dates        || [];
  const impressions = perf.impressions  || [];
  const clicks      = perf.clicks       || [];
  const totalImp    = data?.total_impressions ?? impressions.reduce((a, b) => a + b, 0);
  const totalClk    = data?.total_clicks      ?? clicks.reduce((a, b) => a + b, 0);
  const ctr         = data?.ctr ?? 0;

  const exportCSV = () => {
    const rows = [['Data', 'Impressões', 'Cliques']];
    dates.forEach((d, i) => rows.push([d, impressions[i] ?? 0, clicks[i] ?? 0]));
    const csv  = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `mp_campanha_${campaignId}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const isActive = data?.status === 'active';

  return (
    <DSPageContainer narrow>
      {/* Header */}
      <IceCard noHover>
        <h2 className="mpd-header__title">{camp?.nicho || 'Campanha MP'}</h2>
        <p className="mpd-header__headline">
          {camp?.headline && `"${camp.headline}"`}
        </p>
        <div className="mpd-header__badges">
          <DSBadge color={isActive ? 'green' : 'gray'} dot>
            {isActive ? 'Ativa' : data?.status || 'draft'}
          </DSBadge>
          {camp?.tom && <DSBadge color="purple">{camp.tom}</DSBadge>}
          {camp?.nicho && <DSBadge color="cyan">{camp.nicho}</DSBadge>}
        </div>
      </IceCard>

      {/* Stats */}
      <div className="mpd-stats-grid">
        <StatCard label="Impressões" value={totalImp} color="#06B6D4" />
        <StatCard label="Cliques"    value={totalClk} color="#7E22CE" />
        <StatCard label="CTR"        value={`${ctr}%`} color="#1E40AF" />
      </div>

      {/* Chart */}
      <IceCard noHover>
        <div className="mpd-chart__header">
          <h3 className="mpd-chart__title">Impressões &amp; Cliques por Dia</h3>
          <div className="mpd-chart__legend">
            <span><span className="mpd-chart__dot mpd-chart__dot--imp" /> Impressões</span>
            <span><span className="mpd-chart__dot mpd-chart__dot--clk" /> Cliques</span>
          </div>
        </div>
        <BarChart dates={dates} impressions={impressions} clicks={clicks} />
      </IceCard>

      {totalImp >= 1000 && (
        <IceCard noHover className="mpd-milestone">
          🎉 Sua campanha ultrapassou 1.000 impressões! Parabéns!
        </IceCard>
      )}

      {/* Actions */}
      <div className="mpd-actions">
        <DSButton variant="ghost" onClick={exportCSV}>Exportar CSV</DSButton>
        <DSButton variant="primary" onClick={() => navigate('/mp')}>Nova Campanha</DSButton>
      </div>
    </DSPageContainer>
  );
}

export default function MPDashboardPage() {
  return (
    <AppLayout
      title="Dashboard Marketing Pro"
      subtitle="Performance em tempo real da sua campanha"
      fullWidth
    >
      <MPDashboardContent />
    </AppLayout>
  );
}
